import React from "react";
import api from "../api/client";
import { backdropClose } from "./ModalBackdrop";

/* Browse a device's filesystem and pick a file — so fetching a file doesn't
 * require knowing/typing the exact path. Lists immediate children via the fast
 * /list-dir endpoint (no recursive sizing). Folders navigate; a file is picked
 * via onPick(path). Starts at the drive list. */

type Entry = { name: string; path: string; is_dir: boolean; size: number };

export default function DeviceFileBrowser({
  deviceId,
  onPick,
  onClose,
}: {
  deviceId: number;
  onPick: (path: string) => void;
  onClose: () => void;
}) {
  const [path, setPath] = React.useState(""); // "" = drive list
  const [rows, setRows] = React.useState<Entry[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const pollRef = React.useRef<number | null>(null);

  const fmt = (b: number) => {
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
    if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
    if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${b} B`;
  };

  const list = React.useCallback(
    (target: string) => {
      setLoading(true);
      setError("");
      setPath(target);
      if (pollRef.current) window.clearTimeout(pollRef.current);
      api
        .post(`/api/v1/devices/${deviceId}/list-dir`, { path: target })
        .then((r) => {
          const commandId = r.data.command_id;
          const started = Date.now();
          const poll = () => {
            api
              .get(`/api/v1/devices/${deviceId}/command/${commandId}`)
              .then((cr) => {
                const st = cr.data?.status;
                if (st === "completed") {
                  let parsed: any = [];
                  try {
                    parsed = JSON.parse(cr.data?.result?.stdout || "[]");
                  } catch {
                    parsed = [];
                  }
                  if (!Array.isArray(parsed)) parsed = parsed ? [parsed] : [];
                  setRows(parsed);
                  setLoading(false);
                } else if (st === "failed") {
                  setError(cr.data?.error_message || "Couldn't list that folder.");
                  setLoading(false);
                } else if (Date.now() - started > 120000) {
                  setError("Listing timed out — the folder may be very large or the device busy.");
                  setLoading(false);
                } else {
                  pollRef.current = window.setTimeout(poll, 2000);
                }
              })
              .catch(() => {
                pollRef.current = window.setTimeout(poll, 2000);
              });
          };
          pollRef.current = window.setTimeout(poll, 1500);
        })
        .catch((e) => {
          setError(e.response?.data?.detail || "Couldn't start the listing.");
          setLoading(false);
        });
    },
    [deviceId],
  );

  React.useEffect(() => {
    list("");
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [list]);

  const isDriveRoot = (p: string) => /^[A-Za-z]:\\?$/.test(p.replace(/\\+$/, "\\"));
  const parent = (p: string) => {
    if (isDriveRoot(p)) return ""; // back to the drive list
    const trimmed = p.replace(/\\+$/, "");
    const idx = trimmed.lastIndexOf("\\");
    if (idx <= 2) return trimmed.slice(0, 3); // "C:\"
    return trimmed.slice(0, idx);
  };
  const crumbs = () => {
    if (!path) return [];
    const parts = path.replace(/\\+$/, "").split("\\").filter(Boolean);
    return parts.map((seg, i) => ({
      label: seg,
      full: parts.slice(0, i + 1).join("\\") + (i === 0 ? "\\" : ""),
    }));
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" {...backdropClose(onClose)}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            📂 Browse device — pick a file to fetch
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            ✕
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 flex-wrap px-4 py-2 border-b border-gray-100 dark:border-gray-700/50 text-xs text-gray-500 dark:text-gray-400">
          <button
            onClick={() => list("")}
            disabled={loading}
            className="hover:text-blue-600 dark:hover:text-blue-400 hover:underline disabled:opacity-50"
          >
            This PC
          </button>
          {crumbs().map((c) => (
            <React.Fragment key={c.full}>
              <span className="text-gray-300 dark:text-gray-600">›</span>
              <button
                onClick={() => list(c.full)}
                disabled={loading}
                className="hover:text-blue-600 dark:hover:text-blue-400 hover:underline disabled:opacity-50 font-mono"
              >
                {c.label}
              </button>
            </React.Fragment>
          ))}
        </div>

        {error && <p className="px-4 py-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex-1 overflow-y-auto p-2">
          {loading && (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">Loading…</p>
          )}
          {!loading && rows && (
            <div className="space-y-0.5">
              {path !== "" && (
                <button
                  onClick={() => list(parent(path))}
                  className="w-full flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 text-left"
                >
                  📁 <span className="font-mono">..</span>
                  <span className="text-xs text-gray-400">(up one level)</span>
                </button>
              )}
              {rows.map((r, i) => (
                <button
                  key={i}
                  onClick={() => (r.is_dir ? list(r.path) : onPick(r.path))}
                  className="w-full flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left"
                  title={r.path}
                >
                  <span>{r.is_dir ? "📁" : "📄"}</span>
                  <span className={`flex-1 truncate ${r.is_dir ? "text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-gray-200"}`}>
                    {r.name}
                  </span>
                  {!r.is_dir && (
                    <span className="text-xs font-mono text-gray-400">{fmt(r.size || 0)}</span>
                  )}
                  {!r.is_dir && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Fetch ⬆</span>
                  )}
                </button>
              ))}
              {rows.length === 0 && (
                <p className="text-sm text-gray-400 py-6 text-center">This folder is empty.</p>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-400">
          Click a folder to open it, or a file to fetch it to your computer.
        </div>
      </div>
    </div>
  );
}
